import { STOREFRONT_PATHS } from '@rheora/enrollment-contract';

/**
 * ── WHAT HAPPENS AFTER THE CUSTOMER PRESSES "SIGN AND ENROLL" ───────────────
 *
 * Pure logic for the sign step's COMPLETION PATH, kept out of the `.tsx` per
 * CLAUDE.md. Three separate things live here, and each exists because of a defect
 * measured in a real browser on 2026-08-26:
 *
 *   1. WHAT TO SAY DURING THE WAIT. `POST /bff/sign/accept` was measured at
 *      13.9 s and, in a second walk, 25.4 s. Over that range the page offered a
 *      changed button label and a small spinner. That is not progress reporting —
 *      it is the same "indefinite spinner" the contract viewer already banned
 *      (`agreement-preview.service`), on the one action in the funnel a customer
 *      will re-click if they think it did not land.
 *
 *   2. WHETHER THE STEP IS ALREADY FINISHED. Refreshing `/plan/:slug/sign` after
 *      signing re-rendered the ENTIRE sign surface — the documents, the
 *      acknowledgements, and a LIVE "Sign and enroll" button — inviting the
 *      customer to execute a contract they had already executed. The mutation
 *      result lives in memory; the enrollment does not.
 *
 *   3. WHERE THE SIGNED CONTRACT LIVES. The status token was returned once by the
 *      accept and then discarded, so a refresh left the customer with a signed
 *      agreement and no route to it. `GET /bff/funnel` now carries it (see
 *      `funnel.ts` in the contract package), which is what makes completion
 *      RE-DERIVABLE from server truth rather than remembered.
 */

/**
 * Progress beats for the accept, escalating on a clock.
 *
 * ⚠️ THE STAGES NAME REAL WORK, NOT A FAKE PERCENTAGE. The BFF's accept handler
 * makes three mandatory upstream hops, in this order:
 *
 *   1. Accounts  `POST /v1/us/prospects/:uid/accept` — creates the account, runs
 *      KYC/OFAC, takes the §25.478 deposit decision, registers the payment mandate
 *      and dispatches the enrollment to the market operator. The longest hop.
 *   2. Quotation `POST /v1/us/quotes/:id/account`    — links account to quote.
 *   3. Quotation `POST /v1/us/quotes/:id/accept`     — executes the contract and
 *      records the SignatureEvent.
 *
 * So the copy can describe what is actually happening at each point, which is both
 * more honest and more reassuring than a bar that moves on a timer. The thresholds
 * are deliberately conservative: a fast accept simply never outlives the first
 * beat, and nobody is told about a stage that has not started.
 *
 * ⚠️ THE LAST BEAT IS TERMINAL AND NEVER SAYS "ALMOST DONE". We do not know when
 * it will finish, and inventing a finish line for a 25 s upstream is how a customer
 * decides the page has hung and presses the button again.
 */
export const ACCEPT_LINKING_AFTER_MS = 5_000;
export const ACCEPT_EXECUTING_AFTER_MS = 12_000;

/** i18n keys for the three progress beats, in escalation order. */
export type AcceptProgressKey =
  | 'shop.sign.submit.progress.account'
  | 'shop.sign.submit.progress.linking'
  | 'shop.sign.submit.progress.executing';

/**
 * The progress message for an accept that has been running `elapsedMs`.
 *
 * Monotonic by construction — the thresholds only escalate — so the wait never
 * appears to go backwards, which reads as a bug even when the request is healthy.
 */
export function acceptProgressKey(elapsedMs: number): AcceptProgressKey {
  if (elapsedMs >= ACCEPT_EXECUTING_AFTER_MS) return 'shop.sign.submit.progress.executing';
  if (elapsedMs >= ACCEPT_LINKING_AFTER_MS) return 'shop.sign.submit.progress.linking';
  return 'shop.sign.submit.progress.account';
}

/**
 * The path of the anonymous status page for a given token.
 *
 * Built from the SHARED route constant rather than a template literal, so the SPA
 * and the router cannot disagree about where a signed enrollment lives. The token
 * is URL-encoded: it is server-minted base64url today, but a path builder that
 * assumes its input is safe is a bug waiting for the day the format changes.
 */
export function buildStatusPath(token: string): string {
  return STOREFRONT_PATHS.status.replace(':token', encodeURIComponent(token));
}

/**
 * What the sign STEP should render, decided from server truth first.
 *
 * ── WHY THE SNAPSHOT WINS OVER THE MUTATION ────────────────────────────────
 * The accept mutation's result is browser memory: it is gone on refresh, on a
 * crash, and in a second tab. `GET /bff/funnel` is the server's own answer about
 * whether THIS session's enrollment is signed, and it survives all three. So a
 * completed enrollment is recognised from the snapshot, and the mutation result is
 * used only for the extra detail the snapshot does not carry (the start date and
 * the rescission deadline) — never as the thing that decides "is this done?".
 *
 * ── THE `signed WITHOUT A TOKEN` CASE IS REAL AND MUST NOT BE COLLAPSED ─────
 * The funnel read is CANDIDATE-SCOPED: a shopper who signed and then resolved a
 * different meter gets `signed: false` for the new candidate, which is correct. But
 * `signed: true` with a null token is also reachable (an older session cached
 * before the token was returned over this boundary). It must NOT fall through to
 * `signable`, because that would put a live "Sign and enroll" button in front of an
 * executed contract — the exact defect this function was written for. It resolves
 * to `signed-unlinked`: honest about the enrollment being complete, honest about
 * not having the link.
 */
export type SignStepView =
  /** Nothing signed for this candidate — render the sign surface. */
  | { readonly kind: 'signable' }
  /** Signed, and we can reach the enrollment. */
  | {
      readonly kind: 'signed';
      readonly statusToken: string;
      /** Present only when this tab is the one that did the signing. */
      readonly serviceStartDate: string | null;
      readonly rescissionDeadline: string | null;
    }
  /** Signed, but no status token is available for this session. */
  | { readonly kind: 'signed-unlinked' };

export interface SignStepViewInput {
  /**
   * The accept mutation's result, when THIS tab performed the signing. Optional by
   * design: completion must not depend on it.
   */
  readonly acceptResult:
    | {
        readonly statusToken: string;
        readonly serviceStartDate: string;
        readonly rescissionDeadline: string | null;
      }
    | undefined;
  /** `GET /bff/funnel` → `signed`. Undefined when the snapshot is unavailable. */
  readonly snapshotSigned: boolean | undefined;
  /** `GET /bff/funnel` → `statusToken`. */
  readonly snapshotStatusToken: string | null | undefined;
}

export function resolveSignStepView(input: SignStepViewInput): SignStepView {
  const { acceptResult, snapshotSigned, snapshotStatusToken } = input;

  // This tab just signed. Its result is the richest one available (it alone carries
  // the start date and the rescission deadline), so prefer it — but note that it is
  // an ENRICHMENT of a completion the snapshot also reports, not the trigger.
  if (acceptResult !== undefined) {
    return {
      kind: 'signed',
      statusToken: acceptResult.statusToken,
      serviceStartDate: acceptResult.serviceStartDate,
      rescissionDeadline: acceptResult.rescissionDeadline,
    };
  }

  if (snapshotSigned !== true) return { kind: 'signable' };

  if (snapshotStatusToken === null || snapshotStatusToken === undefined) {
    return { kind: 'signed-unlinked' };
  }

  return {
    kind: 'signed',
    statusToken: snapshotStatusToken,
    // The snapshot deliberately carries neither: they are contract terms, and the
    // status page reads them from the enrollment itself rather than from a cached
    // copy that could disagree with it.
    serviceStartDate: null,
    rescissionDeadline: null,
  };
}
